import { ReactNode } from 'react';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

interface Props {
  title: string | ReactNode;
  caption?: string | ReactNode;
  children?: ReactNode;

  /** Optional filter row (e.g., date range filters) */
  filters?: ReactNode;

  /** Optional overrides */
  className?: string;
  contentClassName?: string;
}

export default function PageHeader({
  title,
  caption,
  children,
  filters,
  className,
  contentClassName
}: Props) {
  const { getColour } = useThemeContext();

  return (
    <div
      className={cn(
        'border-b min-h-18 flex flex-col justify-center',
        className
      )}
      style={{ borderColor: getColour('hairline') }}
    >
      <div
        className={cn(
          'w-full',
          !contentClassName?.includes('px-') && 'px-4 md:px-0',
          !contentClassName?.includes('py-') && 'py-1',
          contentClassName
        )}
      >
        <div className="px-2 flex flex-row items-start md:items-center justify-between gap-3">
          {/* Title and Caption Section */}
          <div className="flex-1 min-w-0">
            {title && (
              <h1
                className={cn(
                  'font-semibold leading-tight',
                  typeof title === 'string'
                    ? 'text-base md:text-xl truncate'
                    : ''
                )}
                style={{ color: getColour('normalText') }}
              >
                {title}
              </h1>
            )}
            {caption && (
              <p
                className="text-xs sm:text-sm mt-0.5 md:mt-1 line-clamp-1 sm:line-clamp-2"
                style={{ color: getColour('inactiveText') }}
              >
                {caption}
              </p>
            )}
          </div>

          {/* Actions Section */}
          {children && (
            <div className="flex flex-row items-center gap-2 flex-shrink-0">
              {children}
            </div>
          )}
        </div>

        {/* Optional Filters Row */}
        {filters && (
          <div
            className="mt-3 pt-3 border-t"
            style={{ borderColor: getColour('border') }}
          >
            {filters}
          </div>
        )}
      </div>
    </div>
  );
}
